import React, { useCallback, useEffect, useMemo, useState } from 'react';
import DataTable from 'react-data-table-component';
import { Eye } from 'react-feather';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Button, Card, CardBody, Col, Input, Row, UncontrolledTooltip } from 'reactstrap';
import debounce from 'lodash.debounce';
import FairMarketsModal from './FairMarketsModal';
import { getFairOrganizations } from '../../ReduxToolkit/Reducers/fairManagement';
import { getCamelCaseWord } from '../../static/brands_wholesalers_data';

// Organization Management > Fair Organizations: fair orgs only, no plan / subscription columns.
// Markets are edited here; the eye opens the fair's seasons and exhibitors.
const FairOrganizationsTab = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [searchText, setSearchText] = useState('');
  const [query, setQuery] = useState('');
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [marketsFor, setMarketsFor] = useState(null);

  const load = useCallback(async () => {
    setIsLoading(true);
    const data = await dispatch(getFairOrganizations(page, perPage, query));
    setIsLoading(false);
    if (data) {
      setRows(data.data || []);
      setTotal(data.count || 0);
    }
  }, [dispatch, page, perPage, query]);

  useEffect(() => {
    load();
  }, [load]);

  const search = useMemo(
    () =>
      debounce((value) => {
        setPage(1);
        setQuery(value.trim());
      }, 500),
    []
  );

  const columns = [
    { name: 'Id', selector: (row) => row.id, width: '80px' },
    {
      name: '',
      width: '50px',
      cell: (row) => (
        <button
          type="button"
          className="btn btn-link p-0"
          title="View seasons and exhibitors"
          onClick={() => navigate(`/fair-organization/${row.id}`, { state: { organization: row } })}
        >
          <Eye size={16} />
        </button>
      ),
    },
    {
      name: 'Name',
      width: '180px',
      // Cut to one line like Organization Management's name; the tooltip shows it all
      cell: (row) => (
        <>
          <div
            id={`fair-name-tooltip-${row.id}`}
            style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          >
            {row.name || '--'}
          </div>
          <UncontrolledTooltip placement="top" target={`fair-name-tooltip-${row.id}`}>
            {row.name || 'N/A'}
          </UncontrolledTooltip>
        </>
      ),
    },
    {
      name: 'Website',
      width: '130px',
      cell: (row) =>
        row.website ? (
          <>
            <a
              id={`fair-website-tooltip-${row.id}`}
              href={/^https?:\/\//i.test(row.website) ? row.website : `https://${row.website}`}
              target="_blank"
              rel="noreferrer"
              style={{ color: '#007bff', textDecoration: 'underline' }}
            >
              Open Website
            </a>
            <UncontrolledTooltip placement="top" target={`fair-website-tooltip-${row.id}`}>
              {row.website}
            </UncontrolledTooltip>
          </>
        ) : (
          '--'
        ),
    },
    {
      name: 'Markets',
      wrap: true,
      grow: 2,
      selector: (row) => (row.fair_location || []).map((c) => getCamelCaseWord(c)).join(', ') || '--',
    },
    { name: 'Seasons', selector: (row) => row.seasons, width: '100px' },
    { name: 'Exhibitors', selector: (row) => row.exhibitors, width: '110px' },
    { name: 'Members', selector: (row) => row.members, width: '100px' },
    { name: 'Owner', selector: (row) => row.owner_email || '--', wrap: true, grow: 2 },
    {
      name: 'Joined',
      width: '120px',
      selector: (row) => (row.created_at ? new Date(row.created_at).toLocaleDateString() : '--'),
    },
    {
      name: 'Actions',
      width: '140px',
      cell: (row) => (
        <Button color="primary" size="sm" outline onClick={() => setMarketsFor(row)}>
          Edit markets
        </Button>
      ),
    },
  ];

  return (
    <Card className="mt-3">
      <CardBody>
        <Row className="mb-3">
          <Col md={4}>
            <Input
              placeholder="Search name, website or member email"
              value={searchText}
              onChange={(e) => {
                setSearchText(e.target.value);
                search(e.target.value);
              }}
            />
          </Col>
        </Row>
        <DataTable
          striped
          pagination
          paginationServer
          data={rows}
          columns={columns}
          progressPending={isLoading}
          paginationTotalRows={total}
          paginationPerPage={perPage}
          paginationDefaultPage={page}
          onChangePage={setPage}
          onChangeRowsPerPage={(count) => {
            setPage(1);
            setPerPage(count);
          }}
        />
      </CardBody>
      <FairMarketsModal
        open={!!marketsFor}
        organization={marketsFor}
        onClose={() => setMarketsFor(null)}
        onSaved={load}
      />
    </Card>
  );
};

export default FairOrganizationsTab;
